import { Typography } from "@material-ui/core";
import ActionDialog from "@saleor/components/ActionDialog";
import Form from "@saleor/components/Form";
import { Order } from "@saleor/dispatch/components/BatchAssignmentListHead";
import {
  batchAssignmentListUrl,
  DispatchListUrlDialog,
  DispatchListUrlQueryParams
} from "@saleor/dispatch/url";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import { maybe } from "@saleor/misc";
import { useSearchRider } from "@saleor/motherOrders/queries";
import { SmopUserTypeCode } from "@saleor/types/globalTypes";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import * as React from "react";

import BatchAssignmentListPage from "../../components/BatchAssignmentListPage";
import { useDispatchAssignRider, useValidateOrder } from "../../mutation";
import { validateOrder, validateOrders } from "../../orderValidation";

interface BatchAssignmentListProps {
  params: DispatchListUrlQueryParams;
}
const dispatchInitialData = {
  order_code: "",
  orders: [],
  riderId: "",
  riderName: ""
};

const BatchAssignmentList: React.FC<BatchAssignmentListProps> = ({
  params
}) => {
  const notify = useNotifier();
  const navigate = useNavigator();
  const {
    search: searchRider,
    loadMore: loadMoreRider,
    result: resultRider
  } = useSearchRider({
    variables: {
      first: 100,
      query: "",
      userType: [SmopUserTypeCode.RIDER]
    }
  });

  const [validateOrderMutation] = useValidateOrder({});
  const [assignRiderDispatch, assignRiderDispatchOpts] = useDispatchAssignRider(
    {}
  );

  const [openModal, closeModal] = createDialogActionHandlers<
    DispatchListUrlDialog,
    DispatchListUrlQueryParams
  >(navigate, batchAssignmentListUrl, params);

  return (
    <>
      <Form initial={dispatchInitialData}>
        {({ data, change, reset }) => {
          const submitForm = async () => {
            const result = await assignRiderDispatch({
              variables: {
                orderIds: data.orders.map(e => e.id),
                riderId: data.riderId
              }
            });

            const {
              data: {
                orderRiderDispatchAssignOrders: {
                  hasErrors,
                  orderErrors: mutationError,
                  // orders,
                  ordersWithError
                }
              }
            } = result;

            if (mutationError.length !== 0) {
              notify({
                status: "error",
                text: mutationError[0].message || "Something Went Wrong"
              });
            } else {
              if (hasErrors) {
                notify({
                  status: "error",
                  text: "Remove rows with error"
                });
                closeModal();

                const orderErrors = Object.keys(JSON.parse(ordersWithError));

                const newOrders = data.orders.map(e => {
                  if (
                    orderErrors.some(
                      elementError => elementError === e.orderCode
                    )
                  ) {
                    return { ...e, isValid: false };
                  } else {
                    return { ...e, isValid: true };
                  }
                });

                // set array of validated orders in form state
                change({
                  target: {
                    name: "orders",
                    value: newOrders
                  }
                });
                return;
              } else {
                notify({
                  status: "success",
                  text: "Rider has been assigned"
                });
                closeModal();
                reset();
                return;
              }
            }
          };

          const disabledAssign = data.orders.some(e => !e.isValid);

          const findOrderById = id => data.orders.find(e => e.id === id);

          const onRemoveItem = () => {
            const temp = data.orders.filter(order => order.id !== params.id);

            change({
              target: {
                name: "orders",
                value: temp
              }
            });

            closeModal();
          };

          return (
            <>
              <BatchAssignmentListPage
                change={change}
                data={data}
                hasMore={maybe(
                  () => resultRider?.data?.search?.pageInfo?.hasNextPage
                )}
                onFetchMore={loadMoreRider}
                loading={resultRider?.loading}
                riders={resultRider?.data?.search?.edges}
                searchRider={searchRider}
                validateOrder={(
                  order_number: string,
                  riderId: string,
                  order_state: Order[]
                ) =>
                  validateOrder(
                    order_number,
                    riderId,
                    order_state,
                    validateOrderMutation,
                    notify
                  )
                }
                validateOrders={(orders, riderId) =>
                  validateOrders(orders, riderId, validateOrderMutation)
                }
                onAssignRider={() => openModal("onAssignRider")}
                onDelete={id => openModal("onDelete", { id })}
                disabledAssign={disabledAssign}
              />
              <ActionDialog
                confirmButtonState={assignRiderDispatchOpts.status}
                open={params.action === "onAssignRider"}
                onClose={closeModal}
                onConfirm={submitForm}
                title="Assign Rider"
                variant="default"
              >
                <Typography variant="body1">
                  Are you sure you want to assign <b>{data.orders.length}</b> to
                  rider <b>{data.riderName}</b> ?
                </Typography>
              </ActionDialog>
              <ActionDialog
                confirmButtonState="default"
                open={params.action === "onDelete"}
                onClose={closeModal}
                onConfirm={() => onRemoveItem()}
                title="Remove Line"
                variant="default"
              >
                <Typography variant="body1">
                  Are you sure you want to remove this Order{" "}
                  <b>{findOrderById(params.id)?.orderCode}</b> line from the
                  list?{" "}
                </Typography>
              </ActionDialog>
            </>
          );
        }}
      </Form>
    </>
  );
};

export default BatchAssignmentList;
